import React, { useState, useEffect, useRef } from 'react';
import ensureMapsLoaded from './loadMaps';
import { useAuth } from './AuthContext';
import { rentApi } from './api';

const RentContributionPage = ({ onRequireLogin, onNavigate }) => {
    const { tokens } = useAuth();
    const [form, setForm] = useState({ rent: '', postType: 'FLAT', bhk: '', furnishing: 'ANY', isAnonymous: false, description: '' });
    const [location, setLocation] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    const searchRef = useRef(null);
    const autocompleteService = useRef(null);
    const placesService = useRef(null);
    const searchTimeout = useRef(null);

    useEffect(() => {
        ensureMapsLoaded().catch(() => {});
        const init = () => {
            if (window.google?.maps?.places) {
                autocompleteService.current = new window.google.maps.places.AutocompleteService();
                const d = document.createElement('div');
                placesService.current = new window.google.maps.places.PlacesService(d);
            }
        };
        if (window.google) init();
        else { const i = setInterval(() => { if (window.google) { init(); clearInterval(i); } }, 200); return () => clearInterval(i); }
    }, []);

    useEffect(() => {
        const h = (e) => { if (searchRef.current && !searchRef.current.contains(e.target)) setShowSuggestions(false); };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, []);

    const handleSearchInput = (e) => {
        const v = e.target.value;
        setSearchQuery(v);
        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        if (!v.trim()) { setSuggestions([]); setShowSuggestions(false); return; }
        setShowSuggestions(true);
        searchTimeout.current = setTimeout(() => {
            if (!autocompleteService.current) return;
            autocompleteService.current.getPlacePredictions(
                { input: v, componentRestrictions: { country: 'in' } },
                (preds, status) => {
                    if (status === window.google.maps.places.PlacesServiceStatus.OK && preds) setSuggestions(preds);
                    else setSuggestions([]);
                }
            );
        }, 400);
    };

    const handleSelectSuggestion = (sugg) => {
        setSearchQuery(sugg.description);
        setShowSuggestions(false);
        if (!placesService.current) return;
        placesService.current.getDetails({ placeId: sugg.place_id, fields: ['geometry'] }, (place, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && place?.geometry?.location) {
                setLocation({
                    name: sugg.structured_formatting?.main_text || sugg.description,
                    placeId: sugg.place_id,
                    latitude: place.geometry.location.lat(),
                    longitude: place.geometry.location.lng(),
                });
            }
        });
    };

    const handleSubmit = async () => {
        if (!tokens?.access) { onRequireLogin(); return; }
        if (!location) { setError('Please select a location'); return; }
        if (!form.rent || Number(form.rent) <= 0) { setError('Please enter a valid rent amount'); return; }
        setError(''); setSubmitting(true);
        try {
            await rentApi.contribute({
                location, rent: Number(form.rent), postType: form.postType,
                bhk: form.bhk ? Number(form.bhk) : null,
                furnishing: form.furnishing === 'ANY' ? null : form.furnishing,
                isAnonymous: form.isAnonymous, description: form.description || null,
            }, tokens.access);
            setSuccess(true);
        } catch (err) { setError(err.message || 'Failed to submit'); }
        finally { setSubmitting(false); }
    };

    const BLUE = '#2563EB', BLUE_DARK = '#1E40AF';
    const postTypes = [{ v: 'FLAT', l: '🏠 Flat', bg: '#EFF6FF', c: '#2563EB' }, { v: 'ROOM', l: '🚪 Room', bg: '#ECFDF5', c: '#059669' }, { v: 'PG', l: '🏢 PG', bg: '#FEF3C7', c: '#D97706' }];

    if (success) return (
        <div style={{ minHeight: '100vh', background: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
            <div style={{ background: 'white', borderRadius: '32px', padding: '48px', textAlign: 'center', maxWidth: '480px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.08)' }}>
                <div style={{ fontSize: '64px', marginBottom: '16px' }}>🎉</div>
                <h2 style={{ fontSize: '28px', fontWeight: '800', color: '#111827', marginBottom: '12px' }}>Thank You!</h2>
                <p style={{ color: '#6B7280', fontSize: '16px', lineHeight: '1.6', marginBottom: '32px' }}>Your rent data helps build transparent pricing for everyone in the community.</p>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                    <button onClick={() => { setSuccess(false); setForm({ rent: '', postType: 'FLAT', bhk: '', furnishing: 'ANY', isAnonymous: false, description: '' }); setLocation(null); setSearchQuery(''); }}
                        style={{ padding: '14px 28px', borderRadius: '14px', background: `linear-gradient(135deg, ${BLUE_DARK}, #3B82F6)`, color: 'white', border: 'none', fontWeight: '700', fontSize: '15px', cursor: 'pointer', boxShadow: '0 4px 14px rgba(34,106,70,0.4)' }}>
                        Add Another
                    </button>
                    <button onClick={() => onNavigate && onNavigate('rentInsights')}
                        style={{ padding: '14px 28px', borderRadius: '14px', background: '#EFF6FF', color: '#475569', border: 'none', fontWeight: '700', fontSize: '15px', cursor: 'pointer' }}>
                        View Insights →
                    </button>
                </div>
            </div>
        </div>
    );

    return (
        <div style={{ minHeight: '100vh', background: '#F3F4F6' }}>
            {/* Header */}
            <header style={{ background: `linear-gradient(155deg, ${BLUE_DARK} 0%, ${BLUE} 55%, #3B82F6 100%)`, padding: '100px 20px 48px', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: '-80px', right: '-80px', width: '300px', height: '300px', background: 'rgba(255,255,255,0.05)', borderRadius: '50%', pointerEvents: 'none' }} />
                <div style={{ maxWidth: '640px', margin: '0 auto', position: 'relative', zIndex: 10 }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(10px)', padding: '5px 16px', borderRadius: '20px', border: '1px solid rgba(255,255,255,0.2)', marginBottom: '12px' }}>
                        <span style={{ fontSize: '12px', color: 'white', fontWeight: '700' }}>🤝 Community Powered</span>
                    </div>
                    <h1 style={{ fontSize: 'clamp(24px, 5vw, 36px)', fontWeight: '800', color: 'white', marginBottom: '8px', letterSpacing: '-0.5px', lineHeight: '1.2' }}>Share Rent Data</h1>
                    <p style={{ color: '#D9E6DC', fontSize: '15px', fontWeight: '400', maxWidth: '480px' }}>Help build transparent rental pricing. Share what you pay — no listing needed.</p>
                </div>
            </header>

            {/* Form */}
            <div style={{ maxWidth: '640px', margin: '-24px auto 40px', padding: '0 20px', position: 'relative', zIndex: 20 }}>
                <div style={{ background: 'white', borderRadius: '28px', padding: '32px', boxShadow: '0 20px 60px rgba(0,0,0,0.08)', border: '1px solid #E5E9E3' }}>

                    {/* Location */}
                    <div style={{ marginBottom: '24px' }}>
                        <label style={labelStyle}>📍 Location *</label>
                        <div style={{ position: 'relative' }} ref={searchRef}>
                            <input type="text" value={searchQuery} onChange={handleSearchInput} onFocus={() => { if (searchQuery) setShowSuggestions(true); }}
                                placeholder="Search your locality..." style={{ ...inputStyle, paddingLeft: '44px' }} />
                            <span style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', fontSize: '18px' }}>🔍</span>
                            {location && <div style={{ marginTop: '8px', padding: '8px 14px', background: '#ECFDF5', borderRadius: '10px', fontSize: '13px', fontWeight: '600', color: '#059669', display: 'flex', alignItems: 'center', gap: '6px' }}>✅ {location.name}</div>}
                            {showSuggestions && suggestions.length > 0 && (
                                <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, background: 'white', borderRadius: '16px', boxShadow: '0 16px 48px rgba(0,0,0,0.15)', zIndex: 100, border: '1px solid #DDE3DC', padding: '6px', maxHeight: '260px', overflowY: 'auto' }}>
                                    {suggestions.map((s, i) => (
                                        <div key={i} onClick={() => handleSelectSuggestion(s)} style={{ padding: '10px 12px', cursor: 'pointer', borderRadius: '10px', fontSize: '14px', fontWeight: '600', color: '#374151', transition: 'background 0.15s' }}
                                            onMouseOver={e => e.currentTarget.style.background = '#EFF6FF'} onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                                            {s.description}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Rent */}
                    <div style={{ marginBottom: '24px' }}>
                        <label style={labelStyle}>💰 Monthly Rent (₹) *</label>
                        <div style={{ position: 'relative' }}>
                            <input type="number" value={form.rent} onChange={e => setForm({ ...form, rent: e.target.value })} placeholder="e.g. 12000" style={{ ...inputStyle, paddingLeft: '36px', fontSize: '20px', fontWeight: '800' }} />
                            <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', fontSize: '18px', fontWeight: '800', color: '#9CA3AF' }}>₹</span>
                        </div>
                    </div>

                    {/* Post Type */}
                    <div style={{ marginBottom: '24px' }}>
                        <label style={labelStyle}>🏷️ Property Type</label>
                        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                            {postTypes.map(t => (
                                <button key={t.v} onClick={() => setForm({ ...form, postType: t.v })}
                                    style={{ padding: '10px 20px', borderRadius: '14px', border: form.postType === t.v ? `2px solid ${t.c}` : '2px solid #DDE3DC', background: form.postType === t.v ? t.bg : '#F8FAFC', color: form.postType === t.v ? t.c : '#6B7280', fontWeight: '700', fontSize: '14px', cursor: 'pointer', transition: 'all 0.2s' }}>
                                    {t.l}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* BHK + Furnishing */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                        <div>
                            <label style={labelStyle}>BHK (optional)</label>
                            <select value={form.bhk} onChange={e => setForm({ ...form, bhk: e.target.value })} style={inputStyle}>
                                <option value="">Select</option>
                                <option value="1">1 BHK</option><option value="2">2 BHK</option><option value="3">3 BHK</option><option value="4">4+ BHK</option>
                            </select>
                        </div>
                        <div>
                            <label style={labelStyle}>Furnishing (optional)</label>
                            <select value={form.furnishing} onChange={e => setForm({ ...form, furnishing: e.target.value })} style={inputStyle}>
                                <option value="ANY">Any</option><option value="FURNISHED">Furnished</option><option value="SEMI">Semi</option><option value="UNFURNISHED">Unfurnished</option>
                            </select>
                        </div>
                    </div>

                    {/* Description */}
                    <div style={{ marginBottom: '24px' }}>
                        <label style={labelStyle}>📝 Notes (optional)</label>
                        <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Any details about the rent..." rows={3}
                            style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }} />
                    </div>

                    {/* Anonymous Toggle */}
                    <div style={{ marginBottom: '28px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px', background: '#F3F4F6', borderRadius: '16px', border: '1px solid #E2E8F0' }}>
                        <div>
                            <div style={{ fontWeight: '700', fontSize: '14px', color: '#334155' }}>🕶️ Stay Anonymous</div>
                            <div style={{ fontSize: '12px', color: '#94A3B8', marginTop: '2px' }}>Your identity won't be shown</div>
                        </div>
                        <div onClick={() => setForm({ ...form, isAnonymous: !form.isAnonymous })}
                            style={{ width: '52px', height: '28px', borderRadius: '14px', background: form.isAnonymous ? '#10B981' : '#CBD5E1', cursor: 'pointer', position: 'relative', transition: 'background 0.2s' }}>
                            <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: 'white', position: 'absolute', top: '3px', left: form.isAnonymous ? '27px' : '3px', transition: 'left 0.2s', boxShadow: '0 2px 6px rgba(0,0,0,0.15)' }} />
                        </div>
                    </div>

                    {error && <div style={{ padding: '12px 16px', background: '#FEF2F2', color: '#DC2626', borderRadius: '12px', fontSize: '14px', fontWeight: '600', marginBottom: '16px' }}>⚠️ {error}</div>}

                    {/* Submit */}
                    <button onClick={handleSubmit} disabled={submitting}
                        style={{ width: '100%', padding: '16px', borderRadius: '16px', background: submitting ? '#94A3B8' : `linear-gradient(135deg, ${BLUE_DARK}, #3B82F6)`, color: 'white', border: 'none', fontWeight: '800', fontSize: '16px', cursor: submitting ? 'not-allowed' : 'pointer', boxShadow: '0 8px 24px rgba(34,106,70,0.35)', transition: 'all 0.2s' }}>
                        {submitting ? '⏳ Submitting...' : '🚀 Share Rent Data'}
                    </button>

                    <p style={{ textAlign: 'center', fontSize: '12px', color: '#9CA3AF', marginTop: '16px' }}>Your data helps others make informed rental decisions</p>
                </div>
            </div>
        </div>
    );
};

const labelStyle = { display: 'block', fontSize: '14px', fontWeight: '700', color: '#334155', marginBottom: '8px' };
const inputStyle = { width: '100%', padding: '13px 16px', borderRadius: '14px', border: '1px solid #E2E8F0', outline: 'none', background: '#F3F4F6', fontSize: '15px', fontWeight: '500', boxSizing: 'border-box', transition: 'border-color 0.2s' };

export default RentContributionPage;
